import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import extend from 'lodash/extend';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { ButtonWithIcon } from '@els/els-react--button';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import RxStructureService from '@Services/RxStructureService';
import SubstanceDetailsContent from '@Components/Results/structure/substance-details/SubstanceDetailsContent';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import ResultsInterface from '@Services/ResultsInterface';

import './SubstanceDetailsModal.scss';

export default class SubstanceDetailsModal extends React.Component {
  static _getRequiredPropsByDatabaseId(databaseId) {
    let reqKeys;

    switch (databaseId) {
      case '1':
        reqKeys = ['pubchemReferences'];
        break;
      case '5':
      case '15':
      case '16':
        reqKeys = ['supplierData'];
        break;
      default:
        reqKeys = ['facts', 'links'];
        break;
    }

    return reqKeys;
  }

  constructor(props) {
    super(props);

    this.spinnerId = `rx-structure-${get(this.props, 'structure.details.id')}-details-${new Date().valueOf()}`;

    this._firstResponse = null;
    this._pendingRequest = null;
    this._reqKeys = this.constructor._getRequiredPropsByDatabaseId(get(this.props, 'structure.databaseId'));

    this.state = {
      isAllDetailsLoaded:
        !isEmpty(get(this.props, 'structure.details')) &&
        reduce(this._reqKeys, (memo, key) => memo && !isEmpty(get(this.props, `structure.${key}`)), true),
    };

    this.onToggle = this.onToggle.bind(this);
    this.viewDetailsHandler = this.viewDetailsHandler.bind(this);
  }

  // eslint-disable-next-line react/sort-comp
  async _loadSubstanceDetails() {
    this._firstResponse = await ResultsDataManage.getSubstancesById({
      structureId: get(this.props, 'structure.details.id'),
      databaseId: get(this.props, 'structure.databaseId'),
    });

    // Render what we have
    if (!this._firstResponse.resultSize) return;

    // Get results
    await new Promise((resolve, reject) => {
      this._pendingRequest = ResultsInterface.getResults(
        {
          resultName: this._firstResponse.resultName,
          databaseId: this._firstResponse.databaseId,
          context: this._firstResponse.context,
          hitSetKey: this._firstResponse.hitSetKey,
          firstItem: 1,
          lastItem: 1,
        },
        true
      ).subscribe({
        next: ({ data: items }) => {
          const substance = items[0] || {};
          extend(get(this.props, 'structure'), pick(substance, ...this._reqKeys));
          this._pendingRequest = null;
          resolve();
        },
        error: reject,
      });
    });
  }

  onToggle(isOpen) {
    if (isOpen) {
      // eslint-disable-next-line react/destructuring-assignment
      if (!this.state.isAllDetailsLoaded) {
        // Loading details
        Spinners.activate(this.spinnerId);

        this._loadSubstanceDetails()
          .then(() => {
            this.setState({ isAllDetailsLoaded: true });
          })
          .finally(() => {
            Spinners.terminate(this.spinnerId);
          })
          .catch(Logger.error);
      }
    } else if (this._pendingRequest) {
      // Cancel pending request
      this._pendingRequest.cancel();
      this._pendingRequest = null;
    }
  }

  viewDetailsHandler() {
    RxStructureService.viewDetails(
      get(this.props, 'reactionId'),
      get(this.props, 'structure.details.id'),
      get(this.props, 'structure.databaseId'),
      this._firstResponse
    );
    // eslint-disable-next-line react/destructuring-assignment
    this.props.closeMenu();
  }

  componentDidMount() {
    // eslint-disable-next-line react/destructuring-assignment
    if (this.props.loadDetailsOnMount) {
      this.onToggle(true);
    }
  }

  // eslint-disable-next-line react/sort-comp
  componentWillUnmount() {
    // Cancel pending request
    if (this._pendingRequest) {
      if (this._pendingRequest.cancel) {
        this._pendingRequest.cancel();
      }
      this._pendingRequest = null;
    }
  }

  componentDidUpdate() {
    // eslint-disable-next-line react/destructuring-assignment
    this.onToggle(this.props.isOpen);
  }

  render() {
    return (
      <div className="substance-view-details substance-view-details--modal">
        <div className="substance-view-details__content" data-disable-autoclose>
          {/* Loading spinner */}
          <RxSpinnerComponent spinnerId={this.spinnerId} spinnerClass="substance-view-details__spinner" />
          {/* Details content, remount on open */}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.props.isOpen && this.state.isAllDetailsLoaded && (
            <SubstanceDetailsContent structure={get(this.props, 'structure')} metadata={get(this.props, 'metadata')} />
          )}
          {/* Go to details button */}
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {this.state.isAllDetailsLoaded && (
            <div className="substance-view-details__footer" role="menuitem">
              <div className="rx-btn-group--right">
                <ButtonWithIcon
                  iconRight
                  sprite="ChevronRight"
                  type="link"
                  linkIconSize="xs"
                  className="e2e-view-details u-els-margin-bottom-1o8"
                  onClick={this.viewDetailsHandler}>
                  View details
                </ButtonWithIcon>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
SubstanceDetailsModal.defaultProps = {
  isOpen: false,
  loadDetailsOnMount: false,
};
SubstanceDetailsModal.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  structure: PropTypes.object.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  reactionId: PropTypes.string,
  closeMenu: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  loadDetailsOnMount: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  metadata: MetadataPropTypesShape,
};
